import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { Row, Col, Spin } from 'antd';
import BarChartComponent from '../Charts/BarChartComponent';
import PieChartComponent from '../Charts/PieChartComponent';

const DealInsights = ({ onDrillDown }) => {
    const { t } = useTranslation();
    const { dealInsights, loadingDealInsights } = useSelector((state) => state.analytics);

    if (loadingDealInsights) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    // Transform data for charts
    const dealsByUserData = dealInsights?.dealsByUser?.map(item => ({
        userName: item.userName,
        count: item.count,
        userId: item.userId
    })) || [];

    const dealsByStageData = dealInsights?.dealsByStage?.map(item => ({
        stageName: item.stageName,
        count: item.count,
        stageId: item.stageId
    })) || [];

    const dealsByContactStatusData = dealInsights?.dealsByContactStatus?.map(item => ({
        statusName: item.statusName,
        count: item.count,
    })) || [];

    const dealsBySourceData = dealInsights?.dealsBySource?.map(item => ({
        sourceName: item.sourceName,
        count: item.count,
    })) || [];

    const dealsByMajorData = dealInsights?.dealsByMajor?.map(item => ({
        label: item.label,
        count: item.count,
    })) || [];

    const dealsByInterestData = dealInsights?.dealsByInterest?.map(item => ({
        label: item.label,
        count: item.count,
    })) || [];

    const openAmountByUserData = dealInsights?.openAmountByUser?.map(item => ({
        userName: item.userName,
        openAmount: item.openAmount,
        userId: item.userId
    })) || [];

    return (
        <div>
            <Row gutter={[16, 16]}>
                {/* Deals by User */}
                <Col span={12}>
                    <BarChartComponent
                        data={dealsByUserData}
                        xAxisKey="userName"
                        yAxisKey="count"
                        title={t('analytics.dealsByUser')}
                        color="#3b82f6"
                        loading={loadingDealInsights}
                        onClick={(data) => {
                            if (data && data.activeLabel) {
                                const item = dealInsights?.dealsByUser?.find(d => d.userName === data.activeLabel);
                                onDrillDown(`${t('analytics.dealsByUser')} - ${data.activeLabel}`, "deals", { dealOwnerId: item?.userId }, item?.records);
                            }
                        }}
                    />
                </Col>

                {/* Deals Won/Lost */}
                <Col span={12}>
                    <PieChartComponent
                        data={dealInsights?.dealsWonLost || []}
                        dataKey="count"
                        nameKey="status"
                        title={t('analytics.dealsWonVsLost')}
                        loading={loadingDealInsights}
                        onClick={(data) => {
                           // Translating Won/Lost
                           const statusLabel = data.name === 'Won' ? t('dashboard.paid') : t('dashboard.notPaid');
                           const item = dealInsights?.dealsWonLost?.find(d => d.status === data.name);
                           onDrillDown(`${t('analytics.dealsWonVsLost')} - ${statusLabel}`, "deals", { reviewStatus: data.name === 'Won' ? 'reviewed' : 'pending_review' }, item?.records);
                        }}
                    />
                </Col>

                {/* Deals by Stage */}
                <Col span={12}>
                    <BarChartComponent
                        data={dealsByStageData}
                        xAxisKey="stageName"
                        yAxisKey="count"
                        title={t('analytics.dealsByStage')}
                        color="#10b981"
                        loading={loadingDealInsights}
                        onClick={(data) => {
                            if (data && data.activeLabel) {
                                const item = dealInsights?.dealsByStage?.find(d => d.stageName === data.activeLabel);
                                const translatedStage = t(`deal.stages.${data.activeLabel}`, { defaultValue: data.activeLabel });
                                onDrillDown(`${t('analytics.dealsByStage')} - ${translatedStage}`, "deals", { dealStageId: item?.stageId }, item?.records);
                            }
                        }}
                    />
                </Col>

                {/* Deals by Contact Status */}
                <Col span={12}>
                    <BarChartComponent
                        data={dealsByContactStatusData}
                        xAxisKey="statusName"
                        yAxisKey="count"
                        title={t('analytics.dealsByContactStatus')}
                        color="#f59e0b"
                        loading={loadingDealInsights}
                        onClick={(data) => {
                            if (data && data.activeLabel) {
                                const item = dealInsights?.dealsByContactStatus?.find(d => d.statusName === data.activeLabel);
                                onDrillDown(`${t('analytics.dealsByContactStatus')} - ${data.activeLabel}`, "deals", { contactStatusId: data.activeLabel }, item?.records);
                            }
                        }}
                    />
                </Col>

                {/* Open Amount by User */}
                <Col span={12}>
                    <BarChartComponent
                        data={openAmountByUserData}
                        xAxisKey="userName"
                        yAxisKey="openAmount"
                        title={t('analytics.openAmountByUsers')}
                        color="#8b5cf6"
                        loading={loadingDealInsights}
                        onClick={(data) => {
                            if (data && data.activeLabel) {
                                const item = dealInsights?.openAmountByUser?.find(d => d.userName === data.activeLabel);
                                onDrillDown(`${t('analytics.openAmountByUsers')} - ${data.activeLabel}`, "deals", { dealOwnerId: item?.userId }, item?.records);
                            }
                        }}
                    />
                </Col>

                {/* Deal Source Distribution */}
                <Col span={12}>
                    <PieChartComponent
                        data={dealsBySourceData}
                        dataKey="count"
                        nameKey="sourceName"
                        title={t('analytics.dealSourceDistribution')}
                        loading={loadingDealInsights}
                        onClick={(data) => {
                            const item = dealInsights?.dealsBySource?.find(d => d.sourceName === data.name);
                            onDrillDown(`${t('analytics.dealSourceDistribution')} - ${data.name}`, "deals", { sourceName: data.name }, item?.records);
                        }}
                    />
                </Col>

                {/* Deals by Major */}
                <Col span={12}>
                    <BarChartComponent
                        data={dealsByMajorData}
                        xAxisKey="label"
                        yAxisKey="count"
                        title={t('analytics.dealsByMajor')}
                        color="#ec4899"
                        loading={loadingDealInsights}
                        onClick={(data) => {
                            if (data && data.activeLabel) {
                                const item = dealInsights?.dealsByMajor?.find(d => d.label === data.activeLabel);
                                onDrillDown(`${t('analytics.dealsByMajor')} - ${data.activeLabel}`, "deals", { majorName: data.activeLabel }, item?.records);
                            }
                        }}
                    />
                </Col>

                {/* Deals by Interest */}
                <Col span={12}>
                    <BarChartComponent
                        data={dealsByInterestData}
                        xAxisKey="label"
                        yAxisKey="count"
                        title={t('analytics.dealsByInterest')}
                        color="#14b8a6"
                        loading={loadingDealInsights}
                        onClick={(data) => {
                            if (data && data.activeLabel) {
                                const item = dealInsights?.dealsByInterest?.find(d => d.label === data.activeLabel);
                                onDrillDown(`${t('analytics.dealsByInterest')} - ${data.activeLabel}`, "deals", { key: data.activeLabel }, item?.records);
                            }
                        }}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default DealInsights;
